import { useDispatch } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationOptions } from "@tanstack/react-query";

import { getProviderProfile as getProviderProfileAction } from "@/redux/actions/auth";
import { UpdateProviderBody } from "@/core/types";

import { providerProfileKeys } from "../keyFactory";
import { updateProviderProfile } from "../queryFunctions/updateProviderProfile";

type Variables = { body: UpdateProviderBody; providerId?: string };

export function useUpdateProviderProfile(
  providerId?: string,
  options: UseMutationOptions<void, unknown, Variables> = {},
) {
  const { onSuccess, ...rest } = options;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProviderProfile,
    ...rest,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: providerProfileKeys.details(providerId) });
      // Keep Redux store in sync for legacy consumers — but only when the mutation targets the
      // logged-in user's own profile (no explicit providerId). Passing a foreign providerId would
      // overwrite auth.profile with another user's data, corrupting the current user's session
      // (menu items, sidebar, etc.). React Query cache invalidation above handles the viewed
      // provider's data regardless.
      // TODO: if dispatch fails, onSuccess still fires — callers should not assume Redux is in sync
      if (!providerId) dispatch(getProviderProfileAction());
      onSuccess?.(data, variables, context);
    },
  });
}
